import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Project : famille "conteneur de travaux".
 *
 * Regroupe dans la durée un objectif, des jalons, un budget et des intervenants,
 * et sert de rattachement aux tâches, temps, dépenses et documents. Construite
 * sur le modèle "famille + type" de la bible (patron : `asset`).
 *
 * Le socle ne connaît pas de projet concret ("chantier", "mission", "dossier") :
 * il connaît la famille générique `project`. Le vocabulaire métier est porté par
 * le champ `type` (chaîne libre déclarée par l'app) ; l'état d'avancement par
 * `status` (chaîne libre, ex. "cadrage", "en_cours", "livré").
 *
 * Références vers d'autres familles = de simples ids (aucun import de schéma) :
 * `contactId` (le client du projet) et `managerWorkerId` (l'intervenant
 * responsable) vivent ici, en ids optionnels. Les liens task, timesheet, expense,
 * order, invoice, document et work-order ne vivent PAS ici : c'est l'autre entité
 * qui porte un `projectId` (un projet regroupe plusieurs de ces pièces, le lien
 * se pose donc du côté enfant, même doctrine que registration).
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs métier seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille project (hors champs standard du socle). */
export const projectCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "chantier", "mission", "dossier"). */
  type: z.string().min(1),
  /** Contact/client pour qui le projet est mené (référence `contact`). Optionnel (un projet interne n'en a pas). */
  contactId: z.string().min(1).optional(),
  /** État d'avancement, chaîne libre pilotée par l'app (ex. "cadrage", "en_cours", "livre", "clos"). Optionnel. */
  status: z.string().min(1).optional(),
  /** Date de démarrage (prévue ou réelle). Optionnel. */
  startAt: z.date().optional(),
  /** Date d'échéance visée. Optionnel. */
  dueAt: z.date().optional(),
  /** Budget financier alloué (montant). Optionnel. */
  budgetAmount: z.number().optional(),
  /** Budget en heures alloué (quantité). Optionnel. */
  budgetHours: z.number().int().optional(),
  /** Jalons du projet (libellé, date cible, état...). Liste structurée, optionnel. */
  milestones: z.array(z.record(z.string(), z.unknown())).optional(),
  /** Intervenant responsable du projet (référence `worker`), optionnel. */
  managerWorkerId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille project (champs standard + champs métier). */
export const ProjectSchema = z.object({
  ...baseEntityFields,
  ...projectCoreFields,
});

/** Type d'un project. */
export type Project = z.infer<typeof ProjectSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const ProjectCreateInputSchema = z.object({
  ...projectCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un project. */
export type ProjectCreateInput = z.infer<typeof ProjectCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const ProjectUpdateInputSchema = z
  .object({ ...projectCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'un project. */
export type ProjectUpdateInput = z.infer<typeof ProjectUpdateInputSchema>;
